import { useState } from 'react';
import type { CustomField } from '@heybesafe/shared';
import { HbsInput } from './hbs/Input';
import { HbsButton } from './hbs/Button';
import { Icon } from './hbs/Icon';
import { EyeToggle } from './hbs/EyeToggle';

interface Props {
  values: CustomField[];
  onChange: (next: CustomField[]) => void;
}

interface RowProps {
  field: CustomField;
  onChange: (next: CustomField) => void;
  onRemove: () => void;
}

function CustomFieldRow({ index, field, onChange, onRemove }: RowProps & { index: number }): JSX.Element {
  const [revealed, setRevealed] = useState(false);
  const masked = !!field.hidden && !revealed;

  return (
    <div className="flex items-start gap-2">
      <div className="flex-1 flex flex-col gap-2">
        <HbsInput
          value={field.name}
          placeholder="Field name (e.g. Account ID)"
          onChange={(name) => onChange({ ...field, name })}
        />
        <HbsInput
          value={field.value}
          placeholder="Value"
          type={masked ? 'password' : 'text'}
          mono={!masked && !!field.hidden}
          onChange={(value) => onChange({ ...field, value })}
          trailingActions={
            <>
              <button
                type="button"
                aria-label={field.hidden ? 'Make field visible' : 'Hide field value'}
                title={field.hidden ? 'Shown in the clear' : 'Hidden (masked) value'}
          id={`custom-field-hide-${index}`}
          onClick={() => onChange({ ...field, hidden: !field.hidden })}
                className={`p-1 rounded-hbs-sm hover:bg-hbs-surface-alt ${field.hidden ? 'text-hbs-primary' : 'text-hbs-text-mute hover:text-hbs-text'}`}
              >
                <Icon name="lock" size={16} />
              </button>
              {field.hidden && <EyeToggle revealed={revealed} onChange={setRevealed} />}
            </>
          }
        />
      </div>
      <button
        type="button"
        aria-label={field.name ? `Remove field: ${field.name}` : 'Remove field'}
          id={`custom-field-remove-${index}`}
          onClick={onRemove}
        className="shrink-0 mt-2.5 p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
      >
        ×
      </button>
    </div>
  );
}

export default function CustomFieldListField({ values, onChange }: Props): JSX.Element {
  function updateAt(i: number, next: CustomField): void {
    const copy = [...values];
    copy[i] = next;
    onChange(copy);
  }

  function removeAt(i: number): void {
    onChange(values.filter((_, j) => j !== i));
  }

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-xs font-medium text-hbs-text-mute uppercase tracking-hbs-overline">
        Custom fields
      </legend>
      {values.map((field, i) => (
        <CustomFieldRow
          key={i}
          index={i}
          field={field}
          onChange={(next) => updateAt(i, next)}
          onRemove={() => removeAt(i)}
        />
      ))}
      <HbsButton
        variant="ghost"
        type="button"
        id="custom-field-add-button"
        onClick={() => onChange([...values, { name: '', value: '' }])}
      >
        + Add field
      </HbsButton>
    </fieldset>
  );
}
